import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Loader2 } from 'lucide-react';
import { DATE_PRESETS, activeDatePreset, type DateRange } from 'lib/dateRange';
import { BottomSheet } from './primitives';
import { M } from './theme';

/**
 * The pieces of a phone filter sheet: filters apply as they are picked and
 * the footer shows how many results they leave.
 */

export interface SheetOption {
  id: string;
  name: string;
}

/** Rows shown before "Show N more". */
const VISIBLE_OPTIONS = 4;

interface FilterSheetProps {
  open: boolean;
  onClose: () => void;
  /** Shown while anything is set; clears every filter in the sheet. */
  onClear?: () => void;
  /** Results under the current filters — updates live behind the sheet. */
  total: number;
  children: React.ReactNode;
}

/** "Filtrlar" with a clear link, the sections, then a lime "Show N results" footer. */
export function FilterSheet({ open, onClose, onClear, total, children }: FilterSheetProps) {
  const { t } = useTranslation();
  return (
    <BottomSheet open={open} onClose={onClose} maxHeight="88%" label={t('filters')}>
      <div className="flex items-center justify-between">
        <h2 className="text-[17px] font-bold" style={{ color: M.ink }}>
          {t('filters')}
        </h2>
        {onClear && (
          <button
            type="button"
            onClick={onClear}
            className="h-9 text-[14px] font-medium hover:underline"
            style={{ color: M.ink2 }}
          >
            {t('clear')}
          </button>
        )}
      </div>

      {children}

      <div className="sticky bottom-0 -mx-[18px] mt-6 px-[18px] pt-3" style={{ background: M.card, borderTop: `1px solid ${M.border}` }}>
        <button
          type="button"
          onClick={onClose}
          className="h-12 w-full rounded-md text-[15px] font-semibold"
          style={{ background: M.primary, color: M.primaryInk }}
        >
          {t('filters_show_results', { n: total })}
        </button>
      </div>
    </BottomSheet>
  );
}

export function SheetSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="mt-5">
      <h3 className="mb-1.5 text-[13px] font-semibold" style={{ color: M.ink2 }}>
        {label}
      </h3>
      {children}
    </section>
  );
}

interface OptionRowsProps {
  name: string;
  options: SheetOption[];
  selected?: string;
  onSelect: (id: string | undefined) => void;
  loading: boolean;
  emptyLabel: string;
}

/** One choice per filter (the APIs take a single value); tap again to clear. */
export function OptionRows({ name, options, selected, onSelect, loading, emptyLabel }: OptionRowsProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  if (loading) {
    return (
      <p className="flex items-center gap-2 py-3 text-[14px]" style={{ color: M.ink3 }}>
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        {t('loading')}
      </p>
    );
  }
  if (options.length === 0) {
    return (
      <p className="py-3 text-[14px]" style={{ color: M.ink3 }}>
        {emptyLabel}
      </p>
    );
  }

  const hidden = options.length - VISIBLE_OPTIONS;
  const shown = expanded || hidden <= 0 ? options : options.slice(0, VISIBLE_OPTIONS);
  return (
    <div role="radiogroup" aria-label={name}>
      {shown.map((option, index) => {
        const isSelected = option.id === selected;
        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => onSelect(isSelected ? undefined : option.id)}
            className="flex h-11 w-full items-center gap-3 text-left text-[15px]"
            style={{ color: M.ink, borderTop: index === 0 ? 'none' : `1px solid ${M.divider}` }}
          >
            <span
              aria-hidden
              className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full"
              style={{ border: `1.5px solid ${isSelected ? M.ink : M.chevron}` }}
            >
              {isSelected && <span className="h-2.5 w-2.5 rounded-full" style={{ background: M.ink }} />}
            </span>
            <span className="min-w-0 flex-1 truncate" style={{ fontWeight: isSelected ? 600 : 400 }}>
              {option.name}
            </span>
          </button>
        );
      })}
      {hidden > 0 && !expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="flex h-8 items-center gap-1 text-[14px] font-medium"
          style={{ color: M.ink2 }}
        >
          {t('load_more_step', { step: hidden })}
          <ChevronDown className="h-4 w-4" aria-hidden />
        </button>
      )}
    </div>
  );
}

/** The date presets as pill chips plus "all dates"; a custom range set elsewhere is spelled out below. */
export function DateRangeChips({ value, onChange }: { value: DateRange; onChange: (range: DateRange) => void }) {
  const { t } = useTranslation();
  const preset = activeDatePreset(value);
  const hasDates = Boolean(value.from || value.to);
  const chips: Array<{ key: string; label: string; active: boolean; apply: () => void }> = [
    ...DATE_PRESETS.map(({ key, from }) => ({
      key,
      label: t(key),
      active: preset === key,
      apply: () => onChange({ from: from(), to: undefined }),
    })),
    { key: 'all', label: t('all_dates'), active: !hasDates, apply: () => onChange({ from: undefined, to: undefined }) },
  ];

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {chips.map((chip) => (
          <button
            key={chip.key}
            type="button"
            aria-pressed={chip.active}
            onClick={chip.apply}
            className="inline-flex h-9 items-center rounded-full px-3.5 text-[14px] font-medium"
            style={
              chip.active
                ? { background: M.ink, color: '#FFFFFF', border: `1px solid ${M.ink}` }
                : { background: M.card, color: M.ink2, border: `1px solid ${M.border}` }
            }
          >
            {chip.label}
          </button>
        ))}
      </div>
      {hasDates && !preset && (
        <p className="mt-2 text-[12.5px]" style={{ color: M.ink3 }}>
          {value.from ?? '…'} – {value.to ?? '…'}
        </p>
      )}
    </>
  );
}
